import React, { useState } from 'react';
import {
  AppBar, Toolbar, Typography, IconButton, InputBase, Box,
  Button, Drawer, List, ListItem, ListItemText, useMediaQuery,
  Tooltip, Avatar, Menu, MenuItem, Divider,
} from '@mui/material';
import {
  Search as SearchIcon, Menu as MenuIcon, Close as CloseIcon,
  Bookmark as BookmarkIcon,
} from '@mui/icons-material';
import { useNavigate, useLocation, Link } from 'react-router-dom';
import { useTheme } from '@mui/material/styles';

const NAV_LINKS = [
  { label: 'Browse', href: '/modules' },
  { label: 'Learning Paths', href: '/learning-paths' },
  { label: 'My Paths', href: '/create-path' },
];

export default function Header() {
  const navigate = useNavigate();
  const location = useLocation();
  const theme = useTheme();
  const isMd = useMediaQuery(theme.breakpoints.up('md'));

  const [drawerOpen, setDrawerOpen] = useState(false);
  const [searchOpen, setSearchOpen] = useState(false);
  const [searchValue, setSearchValue] = useState('');
  const [anchorEl, setAnchorEl] = useState(null);

  const handleSearch = (e) => {
    e.preventDefault();
    if (searchValue.trim()) {
      navigate(`/search?q=${encodeURIComponent(searchValue.trim())}`);
      setSearchValue('');
      setSearchOpen(false);
    }
  };

  const isActive = (href) => location.pathname === href;

  return (
    <AppBar
      position="fixed"
      elevation={0}
      sx={{
        backgroundColor: '#0d1117',
        borderBottom: '1px solid rgba(255,255,255,0.08)',
        zIndex: (t) => t.zIndex.drawer + 1,
      }}
    >
      <Toolbar sx={{ gap: 1, minHeight: 64 }}>
        {/* Logo */}
        <Typography
          component={Link}
          to="/"
          variant="h6"
          sx={{
            fontWeight: 800,
            fontSize: '1.1rem',
            color: '#ffffff',
            textDecoration: 'none',
            letterSpacing: '-0.5px',
            mr: 3,
            flexShrink: 0,
          }}
        >
          Learn<Box component="span" sx={{ color: '#0078d4' }}>Finder</Box>
        </Typography>

        {/* Desktop Nav */}
        {isMd && (
          <Box sx={{ display: 'flex', gap: 0.5, flex: 1 }}>
            {NAV_LINKS.map((l) => (
              <Button
                key={l.href}
                component={Link}
                to={l.href}
                sx={{
                  color: isActive(l.href) ? '#fff' : 'rgba(255,255,255,0.6)',
                  fontWeight: isActive(l.href) ? 600 : 400,
                  fontSize: '0.875rem',
                  textTransform: 'none',
                  px: 1.5,
                  borderRadius: '8px',
                  '&:hover': { color: '#fff', backgroundColor: 'rgba(255,255,255,0.08)' },
                }}
              >
                {l.label}
              </Button>
            ))}
          </Box>
        )}

        <Box sx={{ flex: isMd ? 'none' : 1 }} />

        {/* Search bar (desktop inline, mobile overlay) */}
        {isMd ? (
          <Box
            component="form"
            onSubmit={handleSearch}
            sx={{
              display: 'flex',
              alignItems: 'center',
              backgroundColor: 'rgba(255,255,255,0.08)',
              borderRadius: '8px',
              px: 1.5,
              py: 0.5,
              width: 220,
              '&:focus-within': { backgroundColor: 'rgba(255,255,255,0.12)', outline: '1px solid rgba(255,255,255,0.2)' },
            }}
          >
            <SearchIcon sx={{ color: 'rgba(255,255,255,0.5)', fontSize: 18, mr: 1 }} />
            <InputBase
              placeholder="Search..."
              value={searchValue}
              onChange={(e) => setSearchValue(e.target.value)}
              sx={{ color: '#fff', fontSize: '0.875rem', flex: 1 }}
              inputProps={{ 'aria-label': 'search' }}
            />
          </Box>
        ) : (
          <IconButton onClick={() => setSearchOpen(true)} sx={{ color: 'rgba(255,255,255,0.7)' }}>
            <SearchIcon />
          </IconButton>
        )}

        {/* My Paths shortcut */}
        <Tooltip title="My Learning Paths">
          <IconButton component={Link} to="/create-path" sx={{ color: 'rgba(255,255,255,0.7)' }}>
            <BookmarkIcon />
          </IconButton>
        </Tooltip>

        {/* Profile menu */}
        <IconButton onClick={(e) => setAnchorEl(e.currentTarget)} sx={{ p: 0.5 }}>
          <Avatar sx={{ width: 32, height: 32, backgroundColor: '#0078d4', fontSize: 14 }}>U</Avatar>
        </IconButton>
        <Menu
          anchorEl={anchorEl}
          open={Boolean(anchorEl)}
          onClose={() => setAnchorEl(null)}
          slotProps={{ paper: { sx: { mt: 1.5, minWidth: 160, borderRadius: '10px' } } }}
        >
          <MenuItem component={Link} to="/profile" onClick={() => setAnchorEl(null)}>Profile</MenuItem>
          <Divider />
          <MenuItem onClick={() => setAnchorEl(null)} sx={{ color: 'text.secondary', fontSize: '0.875rem' }}>Sign Out</MenuItem>
        </Menu>

        {/* Mobile hamburger */}
        {!isMd && (
          <IconButton onClick={() => setDrawerOpen(true)} sx={{ color: 'rgba(255,255,255,0.7)', ml: 0.5 }}>
            <MenuIcon />
          </IconButton>
        )}
      </Toolbar>

      {/* Mobile search overlay */}
      {searchOpen && (
        <Box
          component="form"
          onSubmit={handleSearch}
          sx={{
            position: 'fixed', top: 0, left: 0, right: 0, zIndex: 2000,
            backgroundColor: '#0d1117', p: 1, display: 'flex', alignItems: 'center', gap: 1,
          }}
        >
          <SearchIcon sx={{ color: 'rgba(255,255,255,0.5)' }} />
          <InputBase
            autoFocus
            fullWidth
            placeholder="Search modules and learning paths..."
            value={searchValue}
            onChange={(e) => setSearchValue(e.target.value)}
            sx={{ color: '#fff', fontSize: '1rem' }}
          />
          <IconButton onClick={() => setSearchOpen(false)} sx={{ color: 'rgba(255,255,255,0.7)' }}>
            <CloseIcon />
          </IconButton>
        </Box>
      )}

      {/* Mobile Drawer */}
      <Drawer anchor="right" open={drawerOpen} onClose={() => setDrawerOpen(false)}>
        <Box sx={{ width: 240, pt: 2 }}>
          <Box sx={{ display: 'flex', justifyContent: 'flex-end', px: 2, mb: 1 }}>
            <IconButton onClick={() => setDrawerOpen(false)}><CloseIcon /></IconButton>
          </Box>
          <List>
            {NAV_LINKS.map((l) => (
              <ListItem
                key={l.href}
                component={Link}
                to={l.href}
                onClick={() => setDrawerOpen(false)}
                sx={{ color: isActive(l.href) ? 'primary.main' : 'text.primary' }}
              >
                <ListItemText primary={l.label} />
              </ListItem>
            ))}
            <Divider sx={{ my: 1 }} />
            <ListItem component={Link} to="/profile" onClick={() => setDrawerOpen(false)}>
              <ListItemText primary="Profile" />
            </ListItem>
          </List>
        </Box>
      </Drawer>
    </AppBar>
  );
}
